import PublicShell from "@/components/public/PublicShell";
import LeadDialog from "@/components/public/LeadDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { GraduationCap, Building2, Heart, CheckCircle2, ArrowRight } from "lucide-react";

const CONTENT = {
    schools: {
        icon: GraduationCap, eyebrow: "For Schools",
        title: "Bring practical digital & AI education to your school",
        intro: "Institution-level accounts with dashboards for student participation, digital & AI readiness, teacher readiness and program outcomes.",
        img: "https://images.unsplash.com/photo-1759823420520-546e46818322?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        blocks: [
            ["Classes 1–3", "Digital Discovery"], ["Classes 4–5", "Digital Foundation"], ["Classes 6–8", "Digital Creator"],
            ["Classes 9–10", "Future Digital Skills"], ["Classes 11–12", "AI & Career Ready"],
        ],
        features: ["School dashboard & reports", "Student & teacher management", "Digital safety training", "Digital Passports for every learner"],
        source: "school_inquiry",
    },
    corporate: {
        icon: Building2, eyebrow: "Corporate Academy",
        title: "Make your workforce AI-ready",
        intro: "Manage employees, departments, cohorts and certifications. Measure progress with C0–C8 capability levels and the LARUTA Organizational AI Readiness Score (LAIRS).",
        img: "https://images.unsplash.com/photo-1686624386665-4cd01b96d0f6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        blocks: [
            ["C0–C2", "Digital Awareness → Productivity"], ["C3–C4", "AI Ready → AI Practitioner"],
            ["C5–C6", "AI Workflow → Automation Specialist"], ["C7–C8", "AI Leader → Transformation Leader"],
        ],
        features: ["Employee & department management", "Cohort-based training", "AI readiness scoring (LAIRS)", "Progress & governance reports"],
        source: "corporate_inquiry",
    },
    community: {
        icon: Heart, eyebrow: "Community Digital Literacy Mission",
        title: "Empowering 1 million people through digital literacy",
        intro: "Digital Saturdays, community labs, family academies, senior digital independence and programs for women and underserved learners.",
        img: "https://images.unsplash.com/photo-1758270705290-62b6294dd044?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        blocks: [
            ["18,500+", "People reached"], ["1,200+", "Learners trained"], ["Digital Saturday", "Weekly community labs"], ["Sponsored", "Seats for underserved learners"],
        ],
        features: ["Digital Saturday & community labs", "Family Digital Academy", "Senior Digital Independence", "NGO & local business partnerships"],
        source: "community_inquiry",
    },
};

export default function SegmentPage({ variant }) {
    const c = CONTENT[variant];
    const Icon = c.icon;
    return (
        <PublicShell>
            <section className="bg-secondary text-secondary-foreground">
                <div className="container-page py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <p className="overline text-accent flex items-center gap-2"><Icon className="h-4 w-4" /> {c.eyebrow}</p>
                        <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">{c.title}</h1>
                        <p className="mt-5 text-lg text-secondary-foreground/70 leading-relaxed">{c.intro}</p>
                        <LeadDialog source={c.source} title={`${c.eyebrow} — Enquiry`}
                            trigger={<Button size="lg" className="mt-8 rounded-full" data-testid="segment-cta-btn">Talk to our team <ArrowRight className="ml-2 h-4 w-4" /></Button>} />
                    </div>
                    <img src={c.img} alt={c.title} className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]" />
                </div>
            </section>
            <section className="section-pad">
                <div className="container-page grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <div>
                        <h2 className="font-heading text-2xl font-bold">Structured for every level</h2>
                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {c.blocks.map(([k, v]) => (
                                <Card key={k} className="p-5 border-border/70">
                                    <p className="font-heading text-lg font-bold text-primary">{k}</p>
                                    <p className="mt-1 text-sm text-foreground/60">{v}</p>
                                </Card>
                            ))}
                        </div>
                    </div>
                    <div>
                        <h2 className="font-heading text-2xl font-bold">What's included</h2>
                        <ul className="mt-6 space-y-3">
                            {c.features.map((f) => <li key={f} className="flex items-start gap-3 text-foreground/80"><CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" /> {f}</li>)}
                        </ul>
                    </div>
                </div>
            </section>
        </PublicShell>
    );
}
